import React from 'react';
import PropTypes from 'prop-types';
import { Button, ToastContext, Modal } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
// import { useHistory } from 'react-router-dom'; button for variant list
import ProductModel from '../../../../models/productModel';
import RenderFormProduct from '../../detail/settings/components/renderFormProduct';
import { useForm, requiredField } from '@weezevent/weezjs-core';

import { removeDefaultLanguage, getDefaultLanguage, getProductsLanguages } from '../../../organization/cross_selling/list/translationUtils';

export default function CreateProductModal({ organization, openModal, setOpenModal, sync }) {
    const { t } = useTranslation();
    // const history = useHistory(); button for variant list
    const toast = React.useContext(ToastContext);
    const productCopy = typeof openModal === 'object' ? openModal.object : null;

    // We’re not supposed to exceed the maximum number of characters. But we can if the description is done in the old BO
    // and in that case you can't modify the product in the NBO, this error is an indication to dodge run
    const validateDescription = React.useCallback(description => {
        return description.length > 450 ? 'ticket.cross-selling.add.too_many_characters_description' : true;
    }, []);

    const defaultForm = {
        organization: organization.id,
        name: '',
        description: '',
        disabled: false,
        for_buyer: false,
        choose_none_option: false,
        display_quantity: false,
        display_out_of_stock: false,
        display_order: 0,
        category: 2,
        tva: null,
        translations: {
            name: {},
            description: {}
        }
    };

    const [form, setForm, setFormObject, formErrors, formValidate] = useForm(defaultForm, {
        name: requiredField,
        tva: requiredField,
        description: validateDescription
    });

    const resetmodal = React.useCallback(() => {
        let _defaultForm = { ...defaultForm };
        setFormObject({
            ..._defaultForm
        });

        // reset form errors
        Object.keys(formErrors).forEach(key => {
            delete formErrors[key];
        });
    }, [setFormObject, defaultForm]);

    const [submitting, setSubmitting] = React.useState(false);
    const productsLanguages = React.useMemo(() => getProductsLanguages(organization), [organization]);
    const defaultLanguage = React.useMemo(() => getDefaultLanguage(organization, productsLanguages), [organization, productsLanguages]);

    // Initialise form for duplicate action
    React.useEffect(() => {
        if (productCopy) {
            const { translations, ...productCopy } = openModal.object;

            // Adding - copy suffix to all name translations
            productCopy.name = `${productCopy.name} - copy`;
            setFormObject(
                {
                    ...productCopy,
                    productId: productCopy.id,
                    organization: organization.id
                },
                true
            );
        }
    }, [productCopy, organization.id]);

    const onSave = React.useCallback(() => {
        if (!formValidate()) {
            return toast.error(t('ticket.cross-selling.add.form_error'));
        }
        const payload = {
            ...form,
            translations: removeDefaultLanguage(form.translations, defaultLanguage)
        };
        setSubmitting(true);
        return new ProductModel(payload)
            .save({
                organizationId: organization.id
            })
            .then(res => {
                toast.success(t('ticket.cross-selling.add.productAdded'));
                setOpenModal(false);
            })
            .catch(e => {
                toast.error(t(`ticket.cross-selling.add.productAddError`));
            })
            .finally(() => {
                setSubmitting(false);
                sync();
            });
    }, [organization, sync, form, formValidate]);

    const onDuplicate = React.useCallback(() => {
        if (!formValidate()) {
            return toast.error(t('ticket.cross-selling.add.form_error'));
        }

        setSubmitting(true);

        return ProductModel.duplicate_action({
            organizationId: organization.id,
            productId: form.productId,
            name: form.name,
            visible: !form.disabled
        })
            .then(() => {
                toast.success(t('ticket.cross-selling.duplicate.toast-success'));
                setOpenModal(false);
            })
            .catch(e => {
                toast.error(t('ticket.cross-selling.duplicate.toast-error'));
            })
            .finally(() => {
                sync();
                setSubmitting(false);
            });
    }, [organization, productCopy, toast, form, sync]);

    return (
        <>
            <Modal
                open={openModal}
                allowExternalClosing={false}
                onClose={() => {
                    setOpenModal(false);
                    resetmodal();
                }}
                title={productCopy ? t('ticket.cross-selling.duplicate.title') : t('ticket.cross-selling.modalCreateProduct.title')}
                subtitle={productCopy ? t('ticket.cross-selling.duplicate.subtitle') : t('ticket.cross-selling.modalCreateProduct.subtitle')}
                size={'large'}
                submitButton={
                    <>
                        {/* <Button
                            primary
                            inverted
                            isLoading={false}
                            onClick={() => {
                                onSave();
                                history.push(rout for variant list of the product);
                            }}
                            label={t('ticket.cross-selling.modalCreateProduct.save_and_configure')}
                        /> This button will be added when we we will add the new variant list */}
                        <Button
                            primary
                            isLoading={false}
                            onClick={() => {
                                (productCopy ? onDuplicate : onSave)();
                                resetmodal();
                            }}
                            label={t('ticket.common.action.save')}
                        />
                    </>
                }
                cancelButton={
                    <Button
                        onClick={() => {
                            setOpenModal(false);
                            resetmodal();
                        }}
                        label={t('ticket.common.action.cancel')}
                    />
                }
            >
                <RenderFormProduct
                    form={form}
                    setForm={setForm}
                    setFormObject={setFormObject}
                    formErrors={formErrors}
                    formValidate={formValidate}
                    defaultLanguage={defaultLanguage}
                    variants={productCopy?.variants}
                    isLoading={submitting}
                    isDuplicate={Boolean(productCopy)}
                />
            </Modal>
        </>
    );
}

CreateProductModal.propTypes = {
    organization: PropTypes.object.isRequired
};
